// Sopwith 3D - collision detection and kill resolution.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swcollsn.c. Objects are walked in X order; any two
// whose sprite boxes overlap are tested pixel against pixel. Hits are
// collected first and resolved afterwards so the outcome does not depend on
// the order in which pairs happen to be found.

import { BULLIFE, MAX_OBJS, MAX_PLYR, TARGHITCOUNT } from './constants';
import { clampMax } from './fixed';
import type { Game } from './game';
import { initbird } from './hazards';
import { crashpln, hitpln, moveplyr, planeIsFlying } from './plane';
import { powerupCollected, scorepenalty, scorepln, targetDestroyed } from './scoring';
import { EndStatus, Obj, ObjState, ObjType } from './types';
import { crater, initexpl } from './weapons';

export type CollisionEvent =
  | { kind: 'windshot'; ob: Obj }
  | { kind: 'splatox'; ob: Obj }
  | { kind: 'splatbird'; ob: Obj };

const killed: Obj[] = [];
const killer: (Obj | null)[] = [];
const collsdx: number[] = new Array<number>(MAX_PLYR).fill(0);
const collsdy: number[] = new Array<number>(MAX_PLYR).fill(0);
const collsno: Obj[] = [];
let collxadj = 0;
let collyadj = 0;

/** Do the solid pixels of two sprites overlap? */
export function collisionTest(ob1: Obj, ob2: Obj): boolean {
  if (
    (ob1.type === ObjType.PLANE && ob1.state >= ObjState.FINISHED) ||
    (ob2.type === ObjType.PLANE && ob2.state >= ObjState.FINISHED) ||
    (ob1.type === ObjType.EXPLOSION && ob2.type === ObjType.EXPLOSION)
  ) {
    return false;
  }
  const s1 = ob1.symbol!;
  const s2 = ob2.symbol!;

  let x1: number;
  let x2: number;
  let w: number;
  if (ob1.x < ob2.x) {
    x1 = ob2.x - ob1.x;
    x2 = 0;
    w = clampMax(s1.w - x1, s2.w);
  } else {
    x1 = 0;
    x2 = ob1.x - ob2.x;
    w = clampMax(s2.w - x2, s1.w);
  }
  if (w <= 0) {
    return false;
  }

  let y1: number;
  let y2: number;
  let h: number;
  if (ob1.y < ob2.y) {
    y1 = 0;
    y2 = ob2.y - ob1.y;
    h = clampMax(s2.h - y2, s1.h);
  } else {
    y1 = ob1.y - ob2.y;
    y2 = 0;
    h = clampMax(s1.h - y1, s2.h);
  }
  if (h <= 0) {
    return false;
  }

  let d1 = s1.w * y1 + x1;
  let d2 = s2.w * y2 + x2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (s1.data[d1 + x] && s2.data[d2 + x]) {
        return true;
      }
    }
    d1 += s1.w;
    d2 += s2.w;
  }
  return false;
}

/** Test a sprite column by column against the terrain. */
function tstcrash(g: Game, ob: Obj): void {
  const sym = ob.symbol!;
  for (let x = 0; x < sym.w; x++) {
    const y = ob.y - g.ground[x + ob.x];
    if (y >= sym.h) {
      continue;
    }
    if (y < 0 || sym.data[y * sym.w + x]) {
      if (killed.length < 2 * MAX_OBJS) {
        killed.push(ob);
        killer.push(null);
      }
      return;
    }
  }
}

/** A shot that has not moved yet; used so planes cannot hit themselves. */
function isYoungShot(ob: Obj | null): boolean {
  return !!ob && ob.type === ObjType.SHOT && ob.life >= BULLIFE - 1;
}

/** Resolve one collision: `ob` was hit by `obt` (null means the ground). */
function swkill(g: Game, ob: Obj, obt: Obj | null): void {
  const ttype = obt ? obt.type : ObjType.GROUND;
  if ((ttype === ObjType.BIRD || ttype === ObjType.FLOCK) && ob.type !== ObjType.PLANE) {
    return;
  }

  switch (ob.type) {
    case ObjType.BOMB:
    case ObjType.MISSILE:
      initexpl(g, ob, 0);
      ob.life = -1;
      if (!obt) {
        crater(g, ob);
      }
      return;

    case ObjType.SHOT:
      if (!(obt && obt.type === ObjType.PLANE && isYoungShot(ob))) {
        ob.life = 1;
      }
      return;

    case ObjType.STARBURST:
      if (ttype === ObjType.MISSILE || ttype === ObjType.BOMB || !obt) {
        ob.life = 1;
      }
      return;

    case ObjType.EXPLOSION:
      if (!obt) {
        ob.life = 1;
      }
      return;

    case ObjType.BALLOON:
      if (ob.state !== ObjState.FLYING) {
        return;
      }
      if (ttype !== ObjType.PLANE && ttype !== ObjType.SHOT && ttype !== ObjType.BOMB) {
        return;
      }
      ob.state = ObjState.FINISHED;
      ob.onmap = false;
      ob.life = -1;
      initexpl(g, ob, 0);
      targetDestroyed(g, ob, ttype);
      return;

    case ObjType.POWERUP:
    case ObjType.TARGET:
      if (ob.state !== ObjState.STANDING) {
        return;
      }
      if (ttype === ObjType.EXPLOSION || ttype === ObjType.STARBURST) {
        return;
      }
      if (ttype === ObjType.SHOT) {
        // Bullets chip away; it takes more of them at higher levels.
        ob.hitcount += TARGHITCOUNT;
        if (ob.hitcount <= TARGHITCOUNT * (g.gamenum + 1)) {
          return;
        }
      }
      ob.state = ObjState.FINISHED;
      ob.onmap = false;
      if (
        ob.type === ObjType.POWERUP &&
        obt &&
        obt.movef === moveplyr &&
        obt.type === ObjType.PLANE &&
        planeIsFlying(obt.state)
      ) {
        powerupCollected(ob, obt);
      } else {
        initexpl(g, ob, 0);
        if (ob.type === ObjType.TARGET) {
          targetDestroyed(g, ob, ttype);
        }
      }
      return;

    case ObjType.PLANE: {
      const state = ob.state;
      if (isYoungShot(obt)) {
        return;
      }
      if (state === ObjState.CRASHED || ob.endsts === EndStatus.WINNER) {
        return;
      }
      if (ttype === ObjType.POWERUP) {
        return;
      }
      if (ttype === ObjType.STARBURST || (ttype === ObjType.BIRD && ob.athome)) {
        return;
      }

      if (!obt) {
        // Hit the ground.
        if (state === ObjState.FALLING) {
          initexpl(g, ob, 1);
          crater(g, ob);
        } else if (state < ObjState.FINISHED) {
          scorepln(g, ob, ttype);
          initexpl(g, ob, 1);
          crater(g, ob);
        }
        crashpln(ob);
        return;
      }

      if (state >= ObjState.FINISHED) {
        return;
      }

      if (state === ObjState.FALLING) {
        if (ob === g.consoleplayer) {
          if (ttype === ObjType.SHOT) {
            g.collisionEvents.push({ kind: 'windshot', ob });
          } else if (ttype === ObjType.OX) {
            g.collisionEvents.push({ kind: 'splatox', ob });
          } else if (ttype === ObjType.BIRD || ttype === ObjType.FLOCK) {
            g.collisionEvents.push({ kind: 'splatbird', ob });
          }
        }
        return;
      }

      if (
        ttype === ObjType.SHOT ||
        ttype === ObjType.BIRD ||
        ttype === ObjType.OX ||
        ttype === ObjType.FLOCK
      ) {
        if (ob === g.consoleplayer) {
          if (ttype === ObjType.SHOT) {
            g.collisionEvents.push({ kind: 'windshot', ob });
          } else if (ttype === ObjType.OX) {
            g.collisionEvents.push({ kind: 'splatox', ob });
          } else {
            g.collisionEvents.push({ kind: 'splatbird', ob });
          }
        }
        // Wounds only count in the air; a raid on a parked plane is a kill.
        if (g.conf.wounded && !ob.athome) {
          if (ttype === ObjType.SHOT) {
            ob.flightscore.combatwound = true;
          }
          if (state === ObjState.FLYING) {
            ob.state = ObjState.WOUNDED;
            return;
          }
          if (state === ObjState.STALLED) {
            ob.state = ObjState.WOUNDSTALL;
            return;
          }
        }
      } else {
        initexpl(g, ob, 1);
        if (ttype === ObjType.PLANE && collsno.length < MAX_PLYR) {
          // Mid-air collision: both planes bounce apart.
          collxadj = -collxadj;
          collyadj = -collyadj;
          collsdx[collsno.length] = ((ob.dx + obt.dx) >> 1) + collxadj;
          collsdy[collsno.length] = ((ob.dy + obt.dy) >> 1) + collyadj;
          collsno.push(ob);
        }
      }

      hitpln(ob);
      scorepln(g, ob, ttype);
      return;
    }

    case ObjType.BIRD:
      ob.life = scorepenalty(g, ttype, obt, 25) ? -1 : -2;
      return;

    case ObjType.FLOCK:
      if (ttype !== ObjType.FLOCK && ttype !== ObjType.BIRD && ob.state === ObjState.FLYING) {
        for (let i = 0; i < 8; i++) {
          initbird(g, ob, i);
        }
        ob.life = -1;
        ob.state = ObjState.FINISHED;
      }
      return;

    case ObjType.OX:
      if (ob.state !== ObjState.STANDING) {
        return;
      }
      if (ttype === ObjType.EXPLOSION || ttype === ObjType.STARBURST) {
        return;
      }
      scorepenalty(g, ttype, obt, 200);
      ob.state = ObjState.FINISHED;
      return;

    default:
      return;
  }
}

/** Run collision detection for this tick. */
export function swcollsn(g: Game): void {
  killed.length = 0;
  killer.length = 0;
  collsno.length = 0;
  collxadj = 2;
  collyadj = 1;
  if (g.countmove & 1) {
    collxadj = -collxadj;
    collyadj = -collyadj;
  }

  const { topobj, botobj } = g.world;
  for (let ob = topobj.xnext; ob && ob !== botobj; ob = ob.xnext) {
    const sym = ob.symbol;
    if (!sym) continue;
    const xmax = ob.x + sym.w - 1;
    const ymax = ob.y;
    const ymin = ymax - sym.h + 1;

    for (let obp = ob.xnext; obp && obp !== botobj && obp.x <= xmax; obp = obp.xnext) {
      const ps = obp.symbol;
      if (!ps) continue;
      if (
        obp.y >= ymin &&
        obp.y - ps.h + 1 <= ymax &&
        collisionTest(ob, obp) &&
        killed.length < 2 * MAX_OBJS - 1
      ) {
        killed.push(ob);
        killer.push(obp);
        killed.push(obp);
        killer.push(ob);
      }
    }

    const t = ob.type;
    if (
      (t === ObjType.PLANE &&
        ob.state !== ObjState.FINISHED &&
        ob.state !== ObjState.WAITING &&
        ob.y < g.ground[ob.x + 8] + 24) ||
      ((t === ObjType.BOMB || t === ObjType.MISSILE) && ob.y < g.ground[ob.x + 4] + 12)
    ) {
      tstcrash(g, ob);
    }
  }

  for (let i = 0; i < killed.length; i++) {
    swkill(g, killed[i], killer[i]);
  }

  for (let i = 0; i < collsno.length; i++) {
    collsno[i].dx = collsdx[i];
    collsno[i].dy = collsdy[i];
  }
}

/** Kill `ob` as if `killer` had hit it (null = ground). For tests and scripted events. */
export function killObject(g: Game, ob: Obj, by: Obj | null): void {
  swkill(g, ob, by);
}
